import { ArrowUpRight, MoreHorizontal, Pencil } from "lucide-react";
import * as React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import { useHasOrgRole } from "@/providers/auth/hooks";
import { FactList } from "@/shared/components/FactList";
import { FormField, FormRow } from "@/shared/components/FormSection";
import {
  AutoIngestSwitch,
  IngestedBy,
} from "@/shared/components/ingestionRules";
import { PatternChips } from "@/shared/components/PatternChips";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { FormPageHeading } from "@/shared/components/ResourceForm";
import { FormattedDateTime } from "@/shared/datetime";
import {
  autoIngestStateOf,
  ONLY_ADMINS_CHANGE_RULES,
  type IngestionRuleRecord,
} from "@/shared/domain/ingestionRules";
import {
  useDeleteIngestionRule,
  useIngestionRule,
  useUpdateIngestionRule,
} from "@/shared/domain/ingestionRules/hooks";
import { useTriggerV2ById } from "@/shared/domain/triggers/hooks";
import {
  Callout,
  ConfirmDialog,
  DropdownMenu,
  FormSection,
  Spinner,
} from "@/shared/foundation/primitives";
import { RobotoButton } from "@/shared/foundation/primitives/RobotoButton";
import { userFacingErrorMessage } from "@/shared/http";
import { Goto } from "@/shared/navigation";

import { ExistingFilesBackfill } from "./ExistingFilesBackfill";
import styles from "./IngestionRulePages.module.css";
import { IngestionRulesChrome } from "./IngestionRulesChrome";
import { PausedBanner } from "./PausedBanner";

/** Router state the rule's page is opened with right after the rule is saved. */
export interface RuleSavedState {
  ruleSaved: true;
}

/**
 * One ingestion rule, read: which files it matches, who ingests them, and,
 * while it auto-ingests, the matching files uploaded before it. Only org
 * admins may change it.
 */
export function IngestionRuleDetail() {
  const { ruleId } = useParams<{ ruleId: string }>();
  const ruleQuery = useIngestionRule(ruleId);
  const rule = ruleQuery.data;

  return (
    <IngestionRulesChrome title={rule?.name ?? ruleId ?? "Rule"}>
      <div className={styles.page}>
        {ruleQuery.isLoading && <Spinner />}
        {ruleQuery.error !== null && (
          <Callout variant="error">
            {userFacingErrorMessage(ruleQuery.error)}
          </Callout>
        )}
        {rule !== undefined && <RuleOverview rule={rule} />}
      </div>
    </IngestionRulesChrome>
  );
}

function RuleOverview({ rule }: { rule: IngestionRuleRecord }) {
  const navigate = useNavigate();
  const location = useLocation();
  const justSaved =
    (location.state as Partial<RuleSavedState> | null)?.ruleSaved === true;
  const isAdmin = useHasOrgRole("admin");
  const updateRule = useUpdateIngestionRule();
  const deleteRule = useDeleteIngestionRule();
  const [error, setError] = React.useState<unknown>(null);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const state = autoIngestStateOf(rule);

  return (
    <>
      <div className={styles.headingRow}>
        <FormPageHeading
          title={rule.name}
          subtitle="Sessions wait until matching files are ingested."
        />
        {isAdmin && (
          <div className={styles.headingActions}>
            <RobotoButton
              variant="secondary"
              icon={<Pencil aria-hidden="true" />}
              onClick={() =>
                void navigate(Goto.createEditIngestionRuleRoute(rule.rule_id))
              }
            >
              Edit
            </RobotoButton>
            <DropdownMenu>
              <DropdownMenu.Trigger
                render={
                  <RobotoButton
                    variant="secondary"
                    icon={<MoreHorizontal />}
                    aria-label="More actions"
                  />
                }
              />
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item
                  variant="danger"
                  onClick={() => setDeleteOpen(true)}
                >
                  Delete rule&hellip;
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          </div>
        )}
      </div>
      {!isAdmin && (
        <p className={styles.note} data-testid="ingestion-rule-admin-note">
          {ONLY_ADMINS_CHANGE_RULES}
        </p>
      )}
      {error !== null && (
        <Callout variant="error">{userFacingErrorMessage(error)}</Callout>
      )}

      {state === "paused" && rule.invocation !== null && (
        <PausedBanner
          actionName={rule.invocation.action.name}
          onResume={
            isAdmin
              ? () => {
                  setError(null);
                  updateRule.mutate(
                    {
                      ruleId: rule.rule_id,
                      updates: { auto_ingest: true },
                    },
                    { onError: setError },
                  );
                }
              : undefined
          }
          resuming={updateRule.isPending}
        />
      )}

      <FormSection
        title="Which files"
        hint="Checked against files uploaded to any dataset in this organization."
      >
        <FormRow>
          <FormField label="Matches">
            <Patterns patterns={rule.include_patterns} />
          </FormField>
          <FormField label="Except">
            <Patterns patterns={rule.exclude_patterns} variant="exclude" />
          </FormField>
        </FormRow>
      </FormSection>

      <FormSection
        title="What ingests matching files?"
        actions={
          <AutoIngestSwitch rule={rule} label="Auto" onError={setError} />
        }
      >
        <IngestedBy rule={rule} layout="section" />
      </FormSection>

      <ExistingFilesBackfill rule={rule} justSaved={justSaved} />

      <FormSection title="Details">
        <RuleDetails rule={rule} />
      </FormSection>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete ingestion rule?"
        description={`Delete '${rule.name}'${rule.trigger_id === null ? "" : " and its trigger"}? Files uploaded afterwards are not ingested by it, and sessions don't wait for them. Sessions already waiting for files it matched keep waiting, and those files stay marked for ingestion.`}
        confirmLabel="Delete"
        destructive
        pending={deleteRule.isPending}
        onConfirm={() =>
          deleteRule.mutate(
            { ruleId: rule.rule_id },
            {
              onError: (deleteError) => {
                setDeleteOpen(false);
                setError(deleteError);
              },
              onSuccess: () => {
                void navigate(Goto.route.Settings.Ingestion.Root);
              },
            },
          )
        }
      />
    </>
  );
}

function Patterns({
  patterns,
  variant,
}: {
  patterns: readonly string[];
  variant?: "include" | "exclude";
}) {
  return patterns.length === 0 ? (
    <span className={styles.none}>None</span>
  ) : (
    <PatternChips patterns={patterns} variant={variant} />
  );
}

function RuleDetails({ rule }: { rule: IngestionRuleRecord }) {
  const trigger = useTriggerV2ById(rule.trigger_id ?? undefined);
  return (
    <FactList>
      <FactList.Item label="Rule ID" mono>
        {rule.rule_id}
      </FactList.Item>
      <FactList.Item label="Created">
        <FormattedDateTime value={rule.created} /> &middot;{" "}
        <RobotoPrincipalUnderlineLink principal={rule.created_by} />
      </FactList.Item>
      <FactList.Item label="Modified">
        <FormattedDateTime value={rule.modified} /> &middot;{" "}
        <RobotoPrincipalUnderlineLink principal={rule.modified_by} />
      </FactList.Item>
      {rule.description !== null && rule.description !== "" && (
        <FactList.Item label="Description">{rule.description}</FactList.Item>
      )}
      {rule.trigger_id !== null && (
        <FactList.Item label="Trigger">
          <Link
            to={Goto.createTriggerRoute(rule.trigger_id)}
            className={styles.triggerLink}
          >
            {trigger.data?.name ?? rule.trigger_id}
            <ArrowUpRight size={14} aria-hidden="true" />
          </Link>
        </FactList.Item>
      )}
    </FactList>
  );
}
